/**
 * Pinia store for insurance event data management
 *
 * Insurance events are created when a rental car is returned with damage.
 * They track the insurance claim process for damage to rental vehicles.
 *
 * BE API Namespace: api.insuranceEvents
 */

import { defineStore } from 'pinia'
import { useAuthStore } from 'sde-client/stores'
import {
  type InsuranceEvent,
  type InsuranceEventCustomData,
  type InsuranceEventDetailItem,
  type InsuranceEventDetailedListResponse,
  type CreateInsuranceEventRequest
} from '~/api/types/insuranceEvent'
import {
  type InsuranceEventProcessStatus,
  type InsuranceEventStatusHistoryEntry,
  toBEInsuranceEventStatus,
  resolveInsuranceEventProcessStatus
} from '~/utils/insuranceEventProcessStatus'

/**
 * Map BE detailed response item to InsuranceEvent with resolved relations.
 * BE returns responsoblePersons+company for COMPANY customers,
 * but only customer (flat person object) for PERSON customers.
 */
const mapDetailItem = (item: InsuranceEventDetailItem): InsuranceEvent => {
  const event = item.insuranceEvent
  return {
    ...event,
    customData: event.customData || {},
    rentalCarData: item.rentalCar,
    responsiblePersons: item.responsoblePersons || (item.customer ? [item.customer] : undefined),
    companyData: item.company
  }
}

export const useInsuranceEventStore = defineStore({
  id: 'insuranceEvents',

  state: () => ({
    events: {} as Record<string, InsuranceEvent>,
    loading: false,
    initialized: false
  }),

  getters: {
    allEvents: (state) => Object.values(state.events),
    eventCount: (state) => Object.keys(state.events).length,

    // Per-status counts for the dashboard stat cards
    processStatusCounts: (state) => {
      const counts = {
        WAITING_APPRAISAL: 0,
        WAITING_INVOICE: 0,
        WAITING_PAYMENT: 0,
        CLOSED: 0
      }
      for (const e of Object.values(state.events)) {
        const ps = resolveInsuranceEventProcessStatus(e)
        counts[ps]++
      }
      return counts
    },
    waitingAppraisalCount(): number { return this.processStatusCounts.WAITING_APPRAISAL },
    waitingInvoiceCount(): number { return this.processStatusCounts.WAITING_INVOICE },
    waitingPaymentCount(): number { return this.processStatusCounts.WAITING_PAYMENT },
    closedCount(): number { return this.processStatusCounts.CLOSED }
  },

  actions: {
    // ==================== Fetch Operations ====================

    /**
     * Fetch all insurance events with detailed data from API
     * Uses getAllInsuranceEventsDetailed for full related data
     */
    async fetchEvents(api: API) {
      if (!api?.insuranceEvents) {
        console.warn('[insuranceEventStore] api.insuranceEvents not available')
        return
      }

      this.loading = true

      try {
        const response = await api.insuranceEvents.getAllInsuranceEventsDetailed()

        // Response structure: { insuranceEvents: [...], type: "Map" }
        const data = response as unknown as InsuranceEventDetailedListResponse
        const items = data.insuranceEvents || []

        this.$patch(() => {
          items.forEach((item: InsuranceEventDetailItem) => {
            if (!item.insuranceEvent) return
            const eventEntry = mapDetailItem(item)
            this.events[eventEntry.id] = eventEntry
          })
          //this.initialized = true
        })
      } catch (e) {
        console.error('[insuranceEventStore] Failed to fetch events:', e)
      } finally {
        this.loading = false
      }
    },

    /**
     * Get detailed insurance event by ID (includes customer and car data)
     */
    async getDetailed(api: API, id: string): Promise<InsuranceEvent | null> {
      if (!api?.insuranceEvents) {
        console.warn('[insuranceEventStore] api.insuranceEvents not available')
        return null
      }

      const response = await api.insuranceEvents.getInsuranceEventDetailed(id)
      const item = response as unknown as InsuranceEventDetailItem

      // Fallback: if BE returns flat response without .insuranceEvent wrapper
      if (!item.insuranceEvent) {
        item.insuranceEvent = response as unknown as InsuranceEvent
      }

      const eventEntry = mapDetailItem(item)

      this.$patch(() => {
        this.events[eventEntry.id] = eventEntry
      })

      return eventEntry
    },

    // ==================== CRUD Operations ====================

    /**
     * Create a new insurance event
     */
    async create(api: API, data: CreateInsuranceEventRequest): Promise<InsuranceEvent> {
      if (!api?.insuranceEvents) {
        throw new Error('InsuranceEvents API not available')
      }

      this.loading = true

      try {
        const response = await api.insuranceEvents.createInsuranceEvent(
          data.customer,
          data.rentalCar,
          data.eventDate || null,
          data.location || null,
          data.rentalCarDamage || null,
          data.cost || 0,
          data.status,
          data.customData as Record<string, unknown> || null
        )

        const newEvent = response as unknown as InsuranceEvent

        if (newEvent?.id) {
          this.$patch(() => {
            this.events[newEvent.id] = newEvent
          })
        }

        return newEvent
      } catch (e) {
        console.error('[insuranceEventStore] Failed to create event:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    /**
     * Update an existing insurance event
     */
    async update(api: API, id: string, updates: Partial<InsuranceEvent>): Promise<InsuranceEvent | null> {
      if (!api?.insuranceEvents) {
        throw new Error('InsuranceEvents API not available')
      }

      this.loading = true

      try {
        const existing = this.events[id]
        const merged = {
          ...existing,
          ...updates,
          customData: { ...existing?.customData, ...updates.customData }
        }

        await api.insuranceEvents.updateInsuranceEvent(
          id,
          merged.customer,
          merged.rentalCar,
          merged.eventDate || null,
          merged.location || null,
          merged.rentalCarDamage || null,
          merged.cost || 0,
          merged.status,
          merged.customData as Record<string, unknown> || null
        )

        // Fetch fresh data from BE to ensure state consistency
        const freshEvent = await this.getDetailed(api, id)
        return freshEvent
      } catch (e) {
        console.error('[insuranceEventStore] Failed to update event:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    /**
     * Delete an insurance event (requires ADMIN permission)
     */
    async delete(api: API, id: string): Promise<boolean> {
      if (!api?.insuranceEvents) {
        throw new Error('InsuranceEvents API not available')
      }

      this.loading = true

      try {
        await api.insuranceEvents.deleteInsuranceEvent(id)

        this.$patch(() => {
          delete this.events[id]
        })

        return true
      } catch (e) {
        console.error('[insuranceEventStore] Failed to delete event:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    // ==================== Process Status ====================

    /**
     * Mutate processStatus (FE-side lifecycle) and propagate the coarse BE status.
     * Appends an entry to customData.statusHistory[] (BE has no audit entity).
     */
    async setProcessStatus(
      api: API,
      eventId: string,
      newStatus: InsuranceEventProcessStatus,
      extraCustomData?: Record<string, unknown>
    ): Promise<InsuranceEvent | null> {
      const existing = this.events[eventId]
      if (!existing) {
        console.warn('[insuranceEventStore] setProcessStatus: event not found:', eventId)
        return null
      }

      const oldStatus = resolveInsuranceEventProcessStatus(existing)
      if (oldStatus === newStatus && !extraCustomData) return existing

      const auth = useAuthStore()
      const by = auth.user?.display || undefined

      const historyEntry: InsuranceEventStatusHistoryEntry = {
        at: Date.now(),
        by,
        from: oldStatus,
        to: newStatus
      }

      const prevHistory = Array.isArray(existing.customData?.statusHistory)
        ? existing.customData!.statusHistory!
        : []

      const customData: InsuranceEventCustomData = {
        ...existing.customData,
        ...extraCustomData,
        processStatus: newStatus,
        ...(oldStatus !== newStatus && { statusHistory: [...prevHistory, historyEntry] })
      }

      return this.update(api, eventId, {
        status: toBEInsuranceEventStatus(newStatus),
        customData
      })
    },

    // ==================== Convenience Methods ====================

    /**
     * Create insurance event from car return data
     * Called when a rental car is returned with damage
     */
    async createFromCarReturn(
      api: API,
      params: {
        customerId: string
        rentalCarId: string
        rentalCaseId: string
        location: string
        damageDescription: string
        accidentDescription?: string
        cost: number
      }
    ): Promise<InsuranceEvent> {
      const data: CreateInsuranceEventRequest = {
        customer: params.customerId,
        rentalCar: params.rentalCarId,
        eventDate: Date.now(),
        location: params.location,
        rentalCarDamage: params.damageDescription,
        cost: params.cost,
        status: 'REPORTED',
        customData: {
          rentalCaseId: params.rentalCaseId,
          accidentDescription: params.accidentDescription || undefined,
          processStatus: 'WAITING_APPRAISAL'
        }
      }

      return this.create(api, data)
    },

  }
})

// Re-export types
export type {
  InsuranceEvent,
  InsuranceEventStatus,
  InsuranceEventCustomData,
  CreateInsuranceEventRequest,
  RentalCarData,
  ResponsiblePersonData,
  CompanyData
} from '~/api/types/insuranceEvent'

export {
  getInsuranceEventCustomerName,
  getInsuranceEventCarName
} from '~/api/types/insuranceEvent'
